import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Autocomplete, TextField, CircularProgress, Chip, Box, Typography } from '@mui/material';
import { useRadarData } from '../../../hooks/useRadarData';
import useSearch from '../../TrendRadarChart/hooks/useSearch';
import useDebounce from '../../../hooks/useDebounce';

const SkillsFilter = ({ 
  selectedSkills = [], 
  onSkillsChange,
  placeholder = "Select skills..."
}) => {
  const { data: radarData, loading: isLoading } = useRadarData();
  
  // Local search input state for the autocomplete
  const [localSearchInput, setLocalSearchInput] = useState('');
  const debouncedSearchInput = useDebounce(localSearchInput, 300);

  // Use the existing useSearch hook with global search (no subcategory filtering)
  const {
    searchTerm,
    searchResults,
    showResults,
    isGlobalSearch,
    handleSearch,
    handleResultSelect,
    setIsGlobalSearch,
    showNoResultsMessage
  } = useSearch(radarData || {}, null); // null = global search across all categories

  // Set to global search mode
  useEffect(() => {
    setIsGlobalSearch(true);
  }, [setIsGlobalSearch]);

  // Trigger search when debounced input changes
  useEffect(() => {
    if (debouncedSearchInput !== searchTerm) {
      handleSearch({ target: { value: debouncedSearchInput } });
    }
  }, [debouncedSearchInput, searchTerm, handleSearch]);

  // Create options for the autocomplete - use searchResults if searching, otherwise show empty array
  const autocompleteOptions = useMemo(() => {
    if (!localSearchInput.trim()) {
      return []; // Show no options when not searching
    }
    return searchResults || [];
  }, [localSearchInput, searchResults]);

  // Handle search input changes with debouncing
  const handleInputChange = useCallback((event, newValue, reason) => {
    if (reason === 'input') {
      setLocalSearchInput(newValue);
    } else if (reason === 'clear') {
      setLocalSearchInput('');
    }
  }, []);

  // Handle skill selection from autocomplete
  const handleSkillSelect = useCallback((event, newValue) => {
    // Filter out any string values (from freeSolo) and only keep skill objects
    const validSkills = newValue.filter(skill => typeof skill === 'object' && skill.id);
    onSkillsChange(validSkills);
    
    // Clear search input when a skill is selected
    setLocalSearchInput('');
  }, [onSkillsChange]);

  return (
    <Autocomplete
      multiple
      freeSolo
      options={autocompleteOptions}
      getOptionLabel={(option) => option.name || ''}
      value={selectedSkills}
      inputValue={localSearchInput}
      onInputChange={handleInputChange}
      onChange={handleSkillSelect}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={selectedSkills.length === 0 ? placeholder : ""}
          sx={{
            backgroundColor: 'white',
            '& .MuiOutlinedInput-root': {
              minHeight: '40px',
              display: 'flex',
              alignItems: 'center',
              padding: '0 14px',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#2563eb',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#2563eb',
              }
            },
            '& .MuiInputBase-input': {
              padding: '8px 0',
            }
          }}
        />
      )}
      loading={isLoading}
      loadingText="Loading skills from radar..."
      sx={{
        '& .MuiChip-root': {
          backgroundColor: '#EFF6FF',
          color: '#2563EB'
        }
      }}
      filterOptions={(options) => options} // Don't filter, we handle it with Fuse.js
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderOption={(props, option) => (
        <li 
          {...props} 
          key={option.id}
          style={{
            ...props.style,
            borderBottom: '1px solid #E5E7EB'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              paddingBottom: '8px',
              marginBottom: '8px'
            }}
          >
            <Typography 
              variant="body1" 
              sx={{ 
                fontWeight: 600, 
                fontSize: '0.95rem',
                color: 'text.primary'
              }}
            >
              {option.name}
            </Typography>
            {option.subcategory && (
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.secondary',
                  fontSize: '0.8rem',
                  mt: 0.5,
                  lineHeight: 1.2,
                  opacity: 0.8
                }}
              >
                {option.subcategory}
              </Typography>
            )}
          </Box>
        </li>
      )}
      noOptionsText={
        localSearchInput && localSearchInput.trim().length >= 3 
          ? (showNoResultsMessage ? "No matching skills found" : "Searching...")
          : "Type at least 3 characters to search"
      }
    />
  );
};

export default SkillsFilter; 